import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Phone, Menu, X, MessageCircle, ChevronDown } from "lucide-react";
import { useBusinessConfig } from "@/lib/use-business-config";
import { OpenStatus } from "@/components/OpenStatus";
import { trackEvent } from "@/lib/tracking";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";

type Leaf = { to: string; label: string; desc?: string };
type Group = { label: string; to?: string; children?: Leaf[] };

const primary: Leaf = { to: "/", label: "Home" };

const servicesGroup: Group = {
  label: "Services",
  children: [
    { to: "/mot", label: "MOT Testing", desc: "Classes 1, 2, 3, 4, 5 & 7" },
    { to: "/servicing", label: "Servicing", desc: "Interim, full & major" },
    { to: "/diagnostics", label: "Diagnostics", desc: "Dealer-level fault finding" },
    { to: "/mechanical-repairs", label: "Mechanical Repairs", desc: "Brakes, clutch, suspension" },
    { to: "/bodywork", label: "Bodywork", desc: "Panel, paint & SMART repair" },
    { to: "/services/ev-hybrid-servicing", label: "EV & Hybrid", desc: "High-voltage trained" },
    { to: "/services/air-con-regas", label: "Air Con Regas", desc: "R134a & R1234yf" },
  ],
};

const workshopGroup: Group = {
  label: "Workshop",
  children: [
    { to: "/gallery", label: "Gallery", desc: "Recent work in the bays" },
    { to: "/reviews", label: "Reviews", desc: "Verified customer feedback" },
    { to: "/blog", label: "Insights", desc: "Guides & maintenance tips" },
  ],
};

const contactItem: Leaf = { to: "/contact", label: "Contact" };

const mobileLinks: Leaf[] = [
  primary,
  ...servicesGroup.children!,
  ...workshopGroup.children!,
  contactItem,
];

export function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const biz = useBusinessConfig();

  return (
    <header className="sticky top-0 z-50 bg-card/95 backdrop-blur-xl border-b border-border/50 shadow-[0_1px_0_0_oklch(1_0_0/0.5)_inset,0_4px_20px_-6px_oklch(0_0_0/0.08)]">
      {/* Premium credentials bar */}
      <div className="hidden md:block bg-gradient-to-r from-foreground via-foreground to-foreground/95 text-white/95 text-[11px]">
        <div className="mx-auto max-w-7xl px-4 md:px-6 flex items-center justify-between h-8">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 font-semibold tracking-[0.02em]">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_8px_var(--color-primary)]" />
              DVSA-Authorised Station
              <span className="text-white/50 font-normal">·</span>
              <span className="text-white/80 font-normal">V106376</span>
            </span>
            <span className="text-white/25" aria-hidden="true">|</span>
            <span className="text-white/85 tracking-wide">Classes 1 · 2 · 3 · 4 · 5 · 7</span>
          </div>
          <div className="flex items-center gap-4">
            <OpenStatus />
            <span className="text-white/25" aria-hidden="true">|</span>
            <a
              href={`mailto:${biz.email}`}
              className="text-white/85 hover:text-white transition-colors tracking-wide"
              onClick={() => trackEvent("email_click", { location: "header_microbar" })}
            >
              {biz.email}
            </a>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex h-[72px] items-center justify-between gap-6">
          {/* Brand */}
          <div className="flex items-center gap-4 shrink-0">
            <Link to="/" className="shrink-0 flex items-center gap-3">
              <img src="/images/logo-woodseer-autos.png" alt="Woodseer Autos" className="h-9 w-auto" />
              <span className="hidden xl:flex flex-col leading-tight border-l border-border/60 pl-3">
                <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Est. East London</span>
                <span className="text-[11px] tracking-[0.06em] text-foreground/70 font-medium">Independent · Family-Run</span>
              </span>
            </Link>
            <div className="md:hidden">
              <OpenStatus />
            </div>
          </div>

          {/* Desktop nav — centered, refined */}
          <NavigationMenu className="hidden lg:flex flex-1 justify-center">
            <NavigationMenuList className="gap-0.5">
              <NavigationMenuItem>
                <Link
                  to="/"
                  activeProps={{ className: "!text-primary" }}
                  className="px-3.5 py-2 rounded-md text-[12.5px] font-semibold tracking-[0.04em] uppercase text-foreground/70 hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  Home
                </Link>
              </NavigationMenuItem>

              <DropdownGroup group={servicesGroup} />
              <DropdownGroup group={workshopGroup} />

              <NavigationMenuItem>
                <Link
                  to="/contact"
                  activeProps={{ className: "!text-primary" }}
                  className="px-3.5 py-2 rounded-md text-[12.5px] font-semibold tracking-[0.04em] uppercase text-foreground/70 hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  Contact
                </Link>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          {/* Desktop CTAs — premium hierarchy */}
          <div className="hidden lg:flex items-center gap-2 shrink-0">
            <Link
              to="/book"
              onClick={() => trackEvent("service_cta_click", { location: "header" })}
              className="inline-flex items-center rounded-md border border-foreground/15 bg-transparent px-4 py-2 text-[12px] font-semibold tracking-[0.06em] uppercase text-foreground/80 hover:text-foreground hover:border-foreground/35 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Book In
            </Link>
            <a
              href={biz.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("whatsapp_click", { location: "header" })}
              className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-[#25D366]/30 text-[#1da851] hover:bg-[#25D366]/8 hover:border-[#25D366]/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366] focus-visible:ring-offset-2"
              aria-label="Message us on WhatsApp"
            >
              <MessageCircle className="h-4 w-4" aria-hidden="true" />
            </a>
            <a
              href={biz.phoneHref}
              onClick={() => trackEvent("call_click", { location: "header" })}
              aria-label={`Call Woodseer Autos on ${biz.phone}`}
              className="group inline-flex items-center gap-2 rounded-md bg-gradient-to-b from-primary to-primary/90 px-4 py-2.5 text-[12.5px] font-bold tracking-[0.02em] text-primary-foreground hover:from-primary/95 hover:to-primary/85 transition-all shadow-[0_2px_0_0_oklch(0_0_0/0.08),0_6px_20px_-4px_var(--color-primary)] ring-1 ring-inset ring-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <Phone className="h-3.5 w-3.5 transition-transform group-hover:rotate-[-8deg]" aria-hidden="true" />
              <span className="tabular-nums">{biz.phone}</span>
            </a>
          </div>

          {/* Mobile menu */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-md text-foreground hover:bg-foreground/[0.04] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
          >
            {mobileOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
          </button>

        </div>
      </div>

      {/* Hairline accent under header */}
      <div className="hidden lg:block h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

      {/* Mobile nav */}
      {mobileOpen && (
        <div id="mobile-nav" className="lg:hidden border-t border-border/60 bg-card max-h-[calc(100dvh-64px)] overflow-y-auto">
          <nav className="flex flex-col px-4 py-3 gap-0.5">
            {mobileLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                activeProps={{ className: "text-primary bg-primary/8" }}
                className="px-3 py-3 text-[13px] font-semibold tracking-wide text-foreground/80 hover:text-foreground hover:bg-foreground/[0.03] rounded-lg transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <div className="mt-3 pt-3 border-t border-border/60 flex flex-col gap-2 pb-3">
              <Link
                to="/book"
                className="flex items-center justify-center gap-2 rounded-lg border-2 border-primary px-4 py-3.5 text-sm font-bold text-primary"
                onClick={() => { setMobileOpen(false); trackEvent("service_cta_click", { location: "mobile_menu" }); }}
              >
                Request a Booking
              </Link>
              <a
                href={biz.phoneHref}
                onClick={() => trackEvent("call_click", { location: "mobile_menu" })}
                className="flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3.5 text-sm font-bold text-primary-foreground shadow-lg shadow-primary/20"
              >
                <Phone className="h-4 w-4" />
                Call {biz.phone}
              </a>
              <a
                href={biz.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("whatsapp_click", { location: "mobile_menu" })}
                className="flex items-center justify-center gap-2 rounded-lg bg-[#25D366] px-4 py-3.5 text-sm font-bold text-white"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp Us
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

function DropdownGroup({ group }: { group: Group }) {
  return (
    <NavigationMenuItem>
      <NavigationMenuTrigger className="bg-transparent px-3.5 py-2 h-auto text-[12.5px] font-semibold tracking-[0.04em] uppercase text-foreground/70 hover:text-foreground hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent data-[state=open]:text-primary [&>svg]:hidden gap-1">
        {group.label}
        <ChevronDown className="h-3 w-3 opacity-60 transition-transform duration-200 group-data-[state=open]:rotate-180" aria-hidden />
      </NavigationMenuTrigger>
      <NavigationMenuContent>
        <div className="p-3 w-[420px]">
          <div className="mb-2 px-3 pt-1 pb-2 border-b border-border/60">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">{group.label}</div>
          </div>
          <ul className="grid gap-0.5">
            {group.children!.map((child) => (
              <li key={child.to}>
                <NavigationMenuLink asChild>
                  <Link
                    to={child.to}
                    className={cn(
                      "block rounded-md px-3 py-2.5 transition-colors hover:bg-secondary group/item",
                    )}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[13px] font-bold text-foreground tracking-tight">{child.label}</span>
                      <span className="text-[10px] uppercase tracking-wider text-primary opacity-0 group-hover/item:opacity-100 transition-opacity">View →</span>
                    </div>
                    {child.desc && (
                      <div className="text-[11.5px] text-muted-foreground mt-0.5 leading-snug">{child.desc}</div>
                    )}
                  </Link>
                </NavigationMenuLink>
              </li>
            ))}
          </ul>
        </div>
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
}
